import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { useIntl } from "@ai360/core/intl";
import { APIError, HierarchyAPI, SteeringDataAPI } from "@ai360/core";
import { getTheUserGuid } from "~/login/selectors";
import { setApiResult } from "~/core/api/actions";
import { DetailedApiStatus } from "~/i18n-error-messages";

import { Section, SubSection, TextInput, TextArea, SelectInput, Loader, LoaderTypes } from "~/core";

import {
    DragAndDropFileUploader,
    STEERING_DATA_UPLOADER_STATE_KEY,
    actions as uploaderActions,
    selectors as uploaderSelectors,
    models as uploaderModels,
} from "~/action-panel/components/common/drag-and-drop-file-uploader";

import { actions as notificationActions } from "~/notifications";

import { OrgLevelList } from "../org-level-products/add-edit/components/org-level-list";

import { steeringDataActions, steeringDataSelectors } from "./data";
import { SteeringDataChildList } from "./steering-data-child-list";
import { messages } from "./i18n-messages";

import "./steering-data-form.css";
import "../add-edit-shared-responsive.css";

// --- Constants ---

const SUPPORTED_EXTENSIONS = [
    ".pdf",
    ".doc",
    ".docx",
    ".txt",
    ".csv",
    ".xls",
    ".xlsx",
    ".pptx",
    ".zip",
    ".jpg",
    ".jpeg",
    ".png",
];

// A product recommendations sheet is a spreadsheet (nutrient / product / rate /
// priority reason), so only spreadsheet types are accepted for it.
const PRODUCT_RECOMMENDATION_EXTENSIONS = [".xlsx", ".xlsm", ".xls", ".csv"];

const NAME_MAX_LENGTH = 255;
const NOTES_MAX_LENGTH = 2000;
const URL_MAX_LENGTH = 2048;

type SourceType = "file" | "url-text" | "url-files" | "product-recommendations";

// Whether a source type is one of the URL variants (both need a public URL).
const isUrlSourceType = (t: SourceType): boolean => t === "url-text" || t === "url-files";

// Whether a source type is added by uploading files (a record with a Files sublist
// whose uploaded files are its children).
const isFileSourceType = (t?: SourceType): boolean =>
    t === "file" || t === "product-recommendations";

type IHierarchyFilterLevelWithParentsGuid = HierarchyAPI.IHierarchyFilterLevelWithParentsGuid;

// --- URL Validation ---

const PRIVATE_URL_PATTERNS = [/sharepoint\.com/i, /atlassian\.net/i, /confluence/i, /jira/i];

const PRIVATE_IP_PATTERNS = [
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}/,
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}/,
    /^192\.168\.\d{1,3}\.\d{1,3}/,
];

function isWellFormedUrl(value: string): boolean {
    if (!value || value.length > URL_MAX_LENGTH) {
        return false;
    }
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

function isPrivateUrl(value: string): boolean {
    try {
        const url = new URL(value);
        const hostname = url.hostname;

        // Check hostname patterns
        for (const pattern of PRIVATE_URL_PATTERNS) {
            if (pattern.test(hostname)) {
                return true;
            }
        }

        // Check private IP patterns
        for (const pattern of PRIVATE_IP_PATTERNS) {
            if (pattern.test(hostname)) {
                return true;
            }
        }

        return false;
    } catch {
        return false;
    }
}

function validateUrl(value: string): string | null {
    if (!value) {
        return "SteeringData.UrlRequired";
    }
    if (value.length > URL_MAX_LENGTH) {
        return "SteeringData.UrlTooLong";
    }
    if (!isWellFormedUrl(value)) {
        return "SteeringData.UrlMalformed";
    }
    if (isPrivateUrl(value)) {
        return "SteeringData.UrlPrivate";
    }
    return null;
}

// --- Component Props ---

interface ISteeringDataFormProps {
    closePanel: () => void;
    selectedRecord: SteeringDataAPI.ISteeringDataRecord | null;
}

// --- Component ---

export const SteeringDataForm = ({
    closePanel,
    selectedRecord: selectedRecordProp,
}: ISteeringDataFormProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const userGuid = useAppSelector(getTheUserGuid);
    const loading = useAppSelector(steeringDataSelectors.getSteeringDataLoading);

    // The panel is opened with a snapshot of the row. Prefer the live record from
    // the store (kept fresh by real-time list refetches) so fields that change
    // while the panel is open — notably childCount, which flips the Files/Pages
    // sublist on once the first child arrives — stay current instead of frozen at
    // open time. Falls back to the prop if the record isn't in the list yet.
    const records = useAppSelector(steeringDataSelectors.getSteeringDataRecords);
    const selectedRecord = React.useMemo(() => {
        if (!selectedRecordProp) return null;
        const live = records.find(
            (r) => r.steeringDataGuid === selectedRecordProp.steeringDataGuid
        );
        return live ?? selectedRecordProp;
    }, [records, selectedRecordProp]);

    const uploaderState = useAppSelector((state) =>
        uploaderSelectors.getModuleState(state, STEERING_DATA_UPLOADER_STATE_KEY)
    );

    const topLevelEntries: uploaderModels.UploadFileInfo[] = React.useMemo(() => {
        if (!uploaderState?.uploadFileInfoMap) return [];
        return Array.from(uploaderState.uploadFileInfoMap.values()).filter(
            (ufi: uploaderModels.UploadFileInfo) => ufi.parent == null
        );
    }, [uploaderState?.uploadFileInfoMap]);

    const selectedLeaves: uploaderModels.UploadFileInfo[] = React.useMemo(() => {
        const leaves: uploaderModels.UploadFileInfo[] = [];
        for (const entry of topLevelEntries) {
            if (entry.isZipFile()) {
                for (const child of entry.children) {
                    leaves.push(child);
                }
            } else {
                leaves.push(entry);
            }
        }
        return leaves;
    }, [topLevelEntries]);

    const selectedFileCount = selectedLeaves.length;
    const hasFileErrors = (uploaderState?.filesWithErrorsCount ?? 0) > 0;

    const resolveSelectedFiles = useCallback(async (): Promise<File[]> => {
        const zip = await import("@zip.js/zip.js");
        const files: File[] = [];

        for (const entry of topLevelEntries) {
            if (!entry.isZipFile()) {
                if (entry.fileObj) {
                    files.push(entry.fileObj);
                }
                continue;
            }

            const keptChildren = entry.children;
            if (keptChildren.length === 0 || !entry.fileObj) {
                continue;
            }

            const keptByPath = new Map<string, uploaderModels.UploadFileInfo>();
            for (const child of keptChildren) {
                if (child.archiveAbsFileName) {
                    keptByPath.set(child.archiveAbsFileName, child);
                }
            }

            let reader: any;
            try {
                reader = new zip.ZipReader(new zip.BlobReader(entry.fileObj));
                const zipEntries = await reader.getEntries();
                for (const zipEntry of zipEntries) {
                    if (zipEntry.directory) continue;
                    const kept = keptByPath.get(zipEntry.filename);
                    if (!kept || !zipEntry.getData) continue;
                    const blob = await zipEntry.getData(new zip.BlobWriter());
                    files.push(new File([blob], kept.fileName));
                }
            } catch (err) {
                console.error("Failed to extract selected files from ZIP:", err);
            } finally {
                try {
                    await reader?.close();
                } catch {
                    // ignore
                }
            }
        }

        return files;
    }, [topLevelEntries]);

    const isEditMode = !!selectedRecord;

    // Whether this record shows the Files sublist of per-file/per-page children.
    // - file / product-recommendations: always (every upload is a parent, single or
    //   multi, with each uploaded file as a child).
    // - url-files: always (its crawled documents are children).
    // - url-text: only when following its links captured child pages (childCount>0);
    //   a plain single-page url-text record has no sublist.
    // Only meaningful in edit mode (a new record has no children yet).
    const recordSourceType = selectedRecord?.sourceType as SourceType | undefined;
    const showsFilesSublist =
        recordSourceType === "url-files" ||
        isFileSourceType(recordSourceType) ||
        (recordSourceType === "url-text" && (selectedRecord?.childCount ?? 0) > 0);

    // --- Form State ---

    const [name, setName] = useState<string>(selectedRecord?.name || "");
    const [sourceType, setSourceType] = useState<SourceType>(
        (selectedRecord?.sourceType as SourceType) || "file"
    );
    const [publicUrl, setPublicUrl] = useState<string>(selectedRecord?.publicUrl || "");
    const [notes, setNotes] = useState<string>(selectedRecord?.notes || "");

    const sourceTypeOptions = React.useMemo(
        () => [
            { label: formatMessage(messages.fileUploadLabel), value: "file" },
            { label: formatMessage(messages.publicUrlTextLabel), value: "url-text" },
            { label: formatMessage(messages.publicUrlFilesLabel), value: "url-files" },
            {
                label: formatMessage(messages.productRecommendationsLabel),
                value: "product-recommendations",
            },
        ],
        [formatMessage]
    );

    const { data: orgLevelData, isFetching: orgLevelsLoading } =
        HierarchyAPI.useGetSetupOrgLevelListWithParentsGuidQuery(userGuid);
    const orgLevels = React.useMemo(
        () => (orgLevelData?.model ?? []) as IHierarchyFilterLevelWithParentsGuid[],
        [orgLevelData]
    );
    const [selectedOrgLevels, setSelectedOrgLevels] = useState<
        IHierarchyFilterLevelWithParentsGuid[]
    >([]);

    // Validation errors
    const [nameError, setNameError] = useState<string | null>(null);
    const [urlError, setUrlError] = useState<string | null>(null);
    const [hierarchyError, setHierarchyError] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Track submission state. While submitting, the panel stays open and shows its
    // own loader so the upload/processing happens on the details view (not the list).
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [dragAndDropNode, setDragAndDropNode] = useState<HTMLElement | null>(null);
    const dndContainerRef = useCallback((node: HTMLDivElement | null) => {
        setDragAndDropNode(node);
    }, []);

    // --- Configure the shared drag-and-drop uploader ---

    // Re-runs when the steering type changes, which also clears any files picked
    // under the previous type (they may not be valid for the new one).
    useEffect(() => {
        dispatch(
            uploaderActions.setValidExtensions(
                STEERING_DATA_UPLOADER_STATE_KEY,
                sourceType === "product-recommendations"
                    ? PRODUCT_RECOMMENDATION_EXTENSIONS
                    : SUPPORTED_EXTENSIONS
            )
        );
        dispatch(uploaderActions.clearUploadFileList(STEERING_DATA_UPLOADER_STATE_KEY));
        return () =>
            dispatch(uploaderActions.clearUploadFileList(STEERING_DATA_UPLOADER_STATE_KEY));
    }, [dispatch, sourceType]);

    // --- Initialize selected org levels from existing record ---

    // Seed the selected org levels from the record once (keyed on its guid, not the
    // record object). selectedRecord is refreshed from the store on every real-time
    // list refetch, so depending on the object here would reset the user's
    // in-progress hierarchy edits mid-crawl. The guid is stable across refetches.
    const selectedRecordGuid = selectedRecord?.steeringDataGuid;
    useEffect(() => {
        if (isEditMode && selectedRecord?.hierarchyLevels && orgLevels.length > 0) {
            const selectedGuids = new Set(
                selectedRecord.hierarchyLevels.map((level) => level.orgLevelGuid)
            );
            const matched = orgLevels.filter((level) => selectedGuids.has(level.orgLevelGuid));
            setSelectedOrgLevels(matched);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, selectedRecordGuid, orgLevels]);

    // --- Validation ---

    const validateForm = useCallback((): string[] => {
        const errors: string[] = [];

        if (!name.trim()) {
            setNameError("Name is required.");
            errors.push("SteeringData.NameRequired");
        } else if (name.trim().length > NAME_MAX_LENGTH) {
            setNameError(`Name must not exceed ${NAME_MAX_LENGTH} characters.`);
            errors.push("SteeringData.NameTooLong");
        } else {
            setNameError(null);
        }

        // Source type validation
        if (isUrlSourceType(sourceType)) {
            const urlErr = validateUrl(publicUrl);
            if (urlErr) {
                setUrlError(urlErr);
                errors.push(urlErr);
            } else {
                setUrlError(null);
            }
        } else {
            setUrlError(null);
            // File validation. On create, at least one file is required. When
            // editing, files are optional — the record's files live in the sublist,
            // so the user may just be editing name/hierarchy/notes, or adding more
            // files. Any selected files must still be error-free.
            if (selectedFileCount === 0) {
                if (!isEditMode) {
                    errors.push("SteeringData.FileRequired");
                }
            } else if (hasFileErrors) {
                errors.push("SteeringData.FilesHaveErrors");
            }
        }

        // Hierarchy levels validation
        if (selectedOrgLevels.length === 0) {
            setHierarchyError(true);
            errors.push("SteeringData.LocationRequired");
        } else {
            setHierarchyError(false);
        }

        return errors;
    }, [
        name,
        sourceType,
        publicUrl,
        selectedFileCount,
        hasFileErrors,
        selectedOrgLevels,
        isEditMode,
    ]);

    // --- Submit ---

    const onSubmit = useCallback(async () => {
        // Ignore repeat submits while an upload is already in progress.
        if (isSubmitting) {
            return;
        }
        setSubmitError(null);

        const validationErrors = validateForm();
        if (validationErrors.length > 0) {
            const validationError = new APIError("");
            Object.assign(validationError, {
                errorCodeList: validationErrors,
                detailedStatus: DetailedApiStatus.ValidationError,
                apiResultObj: {},
            });
            dispatch(setApiResult({ errorCodeList: validationErrors }));
            dispatch(
                notificationActions.apiCallError(
                    validationError,
                    notificationActions.clearToasterMessages()
                )
            );
            return;
        }

        setIsSubmitting(true);

        const hierarchyLevelGuids = selectedOrgLevels.map((level) => level.orgLevelGuid);

        try {
            if (isEditMode) {
                const resolved = isFileSourceType(sourceType) ? await resolveSelectedFiles() : [];

                // Always persist metadata (name/hierarchy/notes). For a container
                // A file record is always a container parent — we never attach a
                // file to the parent itself — so update only its metadata, and add
                // any selected files as new children.
                dispatch(
                    steeringDataActions.updateSteeringData({
                        steeringDataGuid: selectedRecord.steeringDataGuid,
                        name: name.trim(),
                        sourceType,
                        publicUrl: isUrlSourceType(sourceType) ? publicUrl : undefined,
                        hierarchyLevelGuids,
                        notes: notes.trim() || undefined,
                        file: undefined,
                    })
                );

                if (isFileSourceType(sourceType) && resolved.length > 0) {
                    dispatch(
                        steeringDataActions.addSteeringDataChildren({
                            parentGuid: selectedRecord.steeringDataGuid,
                            files: resolved,
                            hierarchyLevelGuids,
                        })
                    );
                }
            } else if (isFileSourceType(sourceType)) {
                const resolved = await resolveSelectedFiles();

                if (resolved.length === 0) {
                    setSubmitError("No files could be prepared for upload. Please try again.");
                    setIsSubmitting(false);
                    return;
                }

                // A manual upload always becomes a parent + child record(s), even
                // for a single file: one parent (a container carrying no file of
                // its own) with each uploaded file as a child in the sublist.
                dispatch(
                    steeringDataActions.createSteeringDataBatch({
                        files: resolved,
                        baseName: name.trim(),
                        sourceType,
                        hierarchyLevelGuids,
                        notes: notes.trim() || undefined,
                    })
                );
            } else {
                // URL source
                dispatch(
                    steeringDataActions.createSteeringData({
                        name: name.trim(),
                        sourceType,
                        publicUrl,
                        hierarchyLevelGuids,
                        notes: notes.trim() || undefined,
                        file: undefined,
                    })
                );
            }

            // Keep the panel open and submitting; the effect below closes it once
            // the create/upload saga finishes (shared loading returns to false).
        } catch (error) {
            setSubmitError(error?.message || "An error occurred while saving. Please try again.");
            setIsSubmitting(false);
            // Form data is preserved for retry
        }
    }, [
        isSubmitting,
        validateForm,
        isEditMode,
        selectedRecord,
        name,
        sourceType,
        publicUrl,
        notes,
        resolveSelectedFiles,
        selectedOrgLevels,
        dispatch,
    ]);

    // Close the panel once the create/upload saga finishes. The saga flips the
    // shared loading flag true during the upload; we wait until it has actually
    // gone true (sawLoadingRef) and then back to false before closing, so we
    // don't close in the brief window before the saga starts.
    const sawLoadingRef = React.useRef(false);
    useEffect(() => {
        if (!isSubmitting) {
            sawLoadingRef.current = false;
            return;
        }
        if (loading) {
            sawLoadingRef.current = true;
        } else if (sawLoadingRef.current) {
            setIsSubmitting(false);
            sawLoadingRef.current = false;
            if (!submitError) {
                closePanel();
            }
        }
    }, [isSubmitting, loading, submitError, closePanel]);

    // --- Listen for submit event from SlidingPanel header button ---

    useEffect(() => {
        const handleExternalSubmit = () => {
            onSubmit();
        };
        document.addEventListener("steering-data-form-submit", handleExternalSubmit);
        return () => {
            document.removeEventListener("steering-data-form-submit", handleExternalSubmit);
        };
    }, [onSubmit]);

    // --- Hierarchy Level Selection ---

    const onOrgLevelSelectionChange = useCallback(
        (items: IHierarchyFilterLevelWithParentsGuid[]) => {
            setSelectedOrgLevels(items);
            if (items.length > 0) {
                setHierarchyError(false);
            }
        },
        []
    );

    // --- Render ---

    return (
        <div className="add-edit-panel steering-data-add-edit-panel">
            {(loading || isSubmitting) && <Loader type={LoaderTypes.LINE_SCALE} />}

            {submitError && <div className="steering-data-form-error">{submitError}</div>}

            <div className="section-container">
                {/* Left Column: Source type toggle, Name, URL/Notes */}
                <Section>
                    <SubSection>
                        <SelectInput
                            required
                            clearable={false}
                            filterable={false}
                            disabled={isEditMode}
                            options={sourceTypeOptions}
                            placeholderText={formatMessage(messages.steeringTypeLabel)}
                            value={sourceType}
                            onChange={(value?: string) => {
                                if (!isEditMode && value) {
                                    setSourceType(value as SourceType);
                                    setUrlError(null);
                                }
                            }}
                            testId="steering-data-source-type-select"
                        />
                    </SubSection>
                    <SubSection>
                        <TextInput
                            required
                            autoFocus
                            containerClassNames={["form-long-input"]}
                            maxLength={NAME_MAX_LENGTH}
                            placeholderText={formatMessage(messages.nameLabel)}
                            value={name}
                            onChange={(value: string) => {
                                setName(value);
                                if (nameError) setNameError(null);
                            }}
                            hasError={!!nameError}
                        />
                    </SubSection>
                    {isUrlSourceType(sourceType) && (
                        <SubSection>
                            <TextInput
                                required
                                containerClassNames={["form-long-input"]}
                                maxLength={URL_MAX_LENGTH}
                                placeholderText={formatMessage(messages.publicUrlLabel)}
                                value={publicUrl}
                                onChange={(value: string) => {
                                    setPublicUrl(value);
                                    if (urlError) setUrlError(null);
                                }}
                                hasError={!!urlError}
                            />
                        </SubSection>
                    )}
                    <SubSection>
                        <TextArea
                            maxLength={NOTES_MAX_LENGTH}
                            placeholderText={formatMessage(messages.notesLabel)}
                            value={notes}
                            onChange={(value: string) => setNotes(value)}
                        />
                    </SubSection>
                </Section>

                <span className="bar section-fence" />

                {/* Right Column: Location Affiliations */}
                <Section
                    required
                    className="grid-section"
                    headerText={formatMessage(messages.hierarchyLevelsLabel)}
                >
                    {orgLevelsLoading ? (
                        <Loader type={LoaderTypes.LINE_SCALE} />
                    ) : (
                        <OrgLevelList
                            orgLevels={orgLevels}
                            selectedOrgLevels={selectedOrgLevels}
                            onSelectionChange={onOrgLevelSelectionChange}
                            hasError={hierarchyError}
                        />
                    )}
                </Section>
            </div>

            {isFileSourceType(sourceType) && (
                <div className="steering-data-file-section" ref={dndContainerRef}>
                    <DragAndDropFileUploader
                        stateKey={STEERING_DATA_UPLOADER_STATE_KEY}
                        dragAndDropNode={dragAndDropNode}
                    />
                    {isEditMode && selectedRecord?.fileName && selectedFileCount === 0 && (
                        <div className="steering-data-current-file">
                            {formatMessage(messages.currentFile, {
                                fileName: selectedRecord.fileName,
                            })}
                        </div>
                    )}
                    <div className="steering-data-supported-badge">
                        Supported File Types:
                        <br />
                        {sourceType === "product-recommendations"
                            ? ".xlsx, .xls, .csv"
                            : ".pdf, .doc, .docx, .pptx, .xls, .xlsx, .txt, .csv, .zip, .jpg, .png"}
                    </div>
                </div>
            )}

            {/* Files sublist of per-file/per-page children, shown when editing:
                - url-files parents list the documents crawled from the URL.
                - manual-upload (file) parents list each uploaded file.
                - url-text parents list each linked page whose text was captured. */}
            {isEditMode && selectedRecord && showsFilesSublist && (
                <Section
                    className="grid-section steering-data-files-section"
                    headerText={formatMessage(
                        selectedRecord.sourceType === "url-text"
                            ? messages.pagesTitle
                            : messages.filesTitle
                    )}
                >
                    <SteeringDataChildList
                        parentGuid={selectedRecord.steeringDataGuid}
                        sourceType={selectedRecord.sourceType}
                    />
                </Section>
            )}
        </div>
    );
};

export default SteeringDataForm;
